import CheckmarkFilled20 from '@carbon/icons-react/es/checkmark--filled/20';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { WidgetType } from 'api/dashboard/widget';
import { InputFocus } from 'components/atoms/input/input';
import { Caption01, SingleLineEllipsis } from 'components/styles';

const Checked = styled(CheckmarkFilled20)`
  color: var(--primary-color);
`;

interface WidgetTypeSelectProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  selected: boolean;
  value: WidgetType;
  onClick: (value: WidgetType) => void;
}

const Description = styled.div`
  background: var(--gray-100);
  color: var(--white-100);
  > div {
    padding: var(--spacing-03);
  }
  ${Caption01};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--layout-01);
`;

const Title = styled.div`
  flex: 0 0 80%;
  ${SingleLineEllipsis};
`;

const Icon = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;

  svg {
    height: 100%;
  }
`;

const Container = styled.div<{ selected: boolean }>`
  background-color: var(--white-100);
  height: 240px;
  width: 100%;
  padding: var(--spacing-05);
  outline: 2px solid;
  outline-color: transparent;
  outline-offset: -2px;
  box-shadow: var(--shadow-01);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;

  ${({ selected }) =>
    selected
      ? css`
          ${InputFocus};
          ${Description} {
            left: 2px;
            right: 2px;
            bottom: 2px;
          }
        `
      : css`
          ${Description} {
            left: 0;
            right: 0;
            bottom: 0;
          }
        `};

  ${Description} {
    position: absolute;
    height: 0;
    transition: height 300ms cubic-bezier(0.85, 0, 0.15, 1);
    overflow: hidden;
  }

  &:hover {
    box-shadow: var(--shadow-16);
    ${Description} {
      height: 80px;
    }
  }
`;

export const WidgetTypeSelect: FC<WidgetTypeSelectProps> = ({
  icon,
  title,
  selected,
  description,
  onClick,
  value,
}) => {
  return (
    <Container selected={selected} onClick={() => onClick(value)}>
      <Row>
        <Title>{title}</Title>
        {selected ? <Checked /> : null}
      </Row>
      <Icon>{icon}</Icon>
      <Description>
        <div>{description}</div>
      </Description>
    </Container>
  );
};
